<template>
  <table class="table">
    <thead>
      <tr>
        <th>Item</th>
        <th v-if="anyQuantifiable">Quantity</th>
        <th class="text-right">Price</th>
      </tr>
    </thead>

    <tbody>
      <template v-for="estimate in estimates">
        <estimates-table-row
          v-for="item in estimate.invoice_estimate.line_items"
          :key="item.id"
          :amount="item.amount"
          :any-quantifiable="anyQuantifiable"
          :currency="estimate.invoice_estimate.currency_code"
          :discount="item.discount_amount"
          :quantifiable="(productsMap[item.entity_id] || {}).is_quantifiable"
          :quantity="item.quantity"
          :title="item.description"
          :subtitle="formatDateRange(item)"
          :description="(productsMap[item.entity_id] || {}).description"
          @quantity-change="onQuantityChange(item, $event)"
          class="active"
        />
      </template>
    </tbody>

    <tfoot>
      <estimates-table-row
        v-if="taxes.length > 0"
        :amount="invoiceSubtotal"
        :any-quantifiable="anyQuantifiable"
        :bold="true"
        title="Sub Total"
      />
      <estimates-table-row
        v-for="(tax, index) in taxes"
        :key="index"
        :amount="tax.amount"
        :any-quantifiable="anyQuantifiable"
        :bold="true"
        :title="tax.description"
      />
      <estimates-table-row
        :amount="invoiceTotal"
        :any-quantifiable="anyQuantifiable"
        :bold="true"
        title="Total"
        class="info"
      />
      <tr v-if="estimationComplete">
        <td colspan="2">
          <small>Estimate valid for {{ timeLeft }} minutes.</small>
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<script>
import EstimatesTableRow from './EstimatesTableRow';

export default {
  name: 'estimates-table',
  components: {EstimatesTableRow},
  data() {
    return {
      secondsLeft: null,
      timeLeft: null,
      countdownTimer: null,
    };
  },
  props: {
    estimates: {
      type: Array,
      required: true,
    },
    products: {
      type: Array,
      required: true,
    },
    estimationComplete: {
      type: Boolean,
    },
  },
  computed: {
    anyQuantifiable() {
      return this.products.some(product => product.is_quantifiable);
    },
    invoiceSubtotal() {
      return this.invoiceTotal - this.taxesSum;
    },
    invoiceTotal() {
      return this.estimates.reduce((total, estimate) => total + estimate.invoice_estimate.total, 0);
    },
    productsMap() {
      return this.products.reduce((productsMap, product) => {
        productsMap[product.id] = product;
        return productsMap;
      }, {});
    },
    taxes() {
      const allTaxes = this.estimates.reduce((taxes, estimate) => {
        taxes.push(...(estimate.invoice_estimate.taxes ?? []));
        return taxes;
      }, []);

      const groupedTaxesMap = allTaxes.reduce((taxesMap, tax) => {
        taxesMap[tax.name] = {
          ...tax,
          amount: (taxesMap[tax.name]?.amount ?? 0) + tax.amount,
        };

        return taxesMap;
      }, {});

      return Object.values(groupedTaxesMap).filter(tax => tax.amount);
    },
    taxesSum() {
      return this.taxes.reduce((sum, tax) => sum + tax.amount, 0);
    },
  },
  watch: {
    estimationComplete: {
      immediate: true,
      handler(newVal, oldVal) {
        this.startCountdown();
      },
    },
  },
  methods: {
    formatDate(secondsSinceEpoch) {
      const momentTime = moment(secondsSinceEpoch * 1000);
      return (window.SK_USER_TIMEZONE ? momentTime.tz(window.SK_USER_TIMEZONE) : momentTime).format('L');
    },
    formatDateRange(item) {
      if (item.date_from === item.date_to) {
        return '';
      }

      const dateFrom = this.formatDate(item.date_from);
      const dateTo = this.formatDate(item.date_to);

      return `${dateFrom} - ${dateTo}`;
    },
    onQuantityChange({entity_id}, quantity) {
      this.$emit('item-change', {id: entity_id, quantity});
    },
    startCountdown() {
      if (this.estimationComplete) {
        this.secondsLeft = 300;
        this.timeLeft = '05:00';

        this.countdownTimer = setInterval(() => {
          this.secondsLeft -= 1;

          if (this.secondsLeft === 0) {
            // Finished
            this.timeLeft = '00:00';
            clearTimeout(this.countdownTimer);
            location.reload();
          } else {
            // Countdown still going
            let minutes = Math.floor(this.secondsLeft / 60);
            let seconds = this.secondsLeft - minutes * 60;
            this.timeLeft = minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0');
          }
        }, 1000);
      } else {
        clearTimeout(this.countdownTimer);
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
